import React, { useEffect, useState } from 'react';
import { Empty, Table, Tag } from 'antd';
import { useNavigate } from 'react-router-dom';
import Column from 'antd/es/table/Column';
import axiosInstance from '../utils/axiosInstance';
import { formatDate } from '../utils/dateUtil';
import { runSafely } from '../utils/runSafely';
import Loader from '../Components/Loader';
import { useTranslation } from 'react-i18next';
import AppShell from '../Components/AppShell';
import PageHeader from '../Components/PageHeader';
import Container from '../Components/Container';

interface HistoryType {
    key: React.Key;
    id: number;
    date: string;
    amount: string;
    status: string;
}

const WithdrawalHistoryPage: React.FC = () => {
    const [histories, setHistories] = useState<HistoryType[]>([]);
    const [loading, setLoading] = useState(false);

    const navigate = useNavigate();
    const { t } = useTranslation();

    const getUserWithdrawal = async () => {
        setLoading(true);
        const res = await axiosInstance.get("/protected/getUserWithdrawal")
        const data = res.data.data;
        if (data) {
            const withdrawals = data.withdrawals;

            setHistories(withdrawals?.map((e: any) => {
                return {
                    key: e.id,
                    id: e.id,
                    date: formatDate(new Date(e.createdAt)),
                    amount: e.totalAmount,
                    status: e.withdrawalStatus,
                }
            }) || []);
        } else {
            navigate("/login")
        }
        setTimeout(() => {
            setLoading(false);
        }, 1500);

    }

    // Mount-only fetch; see AccountPage for why the dependency is omitted.
    useEffect(() => {
        runSafely('WithdrawalHistoryPage.getUserWithdrawal', getUserWithdrawal, () => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return (
        <AppShell
            header={<PageHeader title={t("accountInfo")} onBack={() => navigate(-1)} />}
        >
            {loading && <Loader />}
            <Container>
                <div className='p-6 flex flex-col gap-5'>
                    <div className='flex flex-col gap-4 lg:hidden'>
                        {histories.map(e => (
                            <div className='flex flex-col gap-2 bg-white p-4 rounded-[1rem]' key={e.key}>
                                <div className='flex justify-between'>
                                    <div className='text-[14px] text-brand-600'>{t("date")}</div>
                                    <div className='text-[14px]'>{e.date}</div>
                                </div>
                                <div className='flex justify-between'>
                                    <div className='text-[14px] text-brand-600'>{`${t("amount")} (RM)`}</div>
                                    <div className='text-[16px] font-bold'>{e.amount}</div>
                                </div>
                                <div className='flex justify-between items-center'>
                                    <div className='text-[14px] text-brand-600'>{t("status")}</div>
                                    <Tag className='m-0 mb-1' color={e.status === "completed" ? "green" : (
                                        e.status === "rejected" ? "red" : "gold"
                                    )}>{t(e.status)?.toUpperCase()}</Tag>
                                </div>
                            </div>
                        ))}
                        {!histories.length && <Empty className='mt-10' />}
                    </div>

                    <div className='hidden lg:block'>
                        <Table<HistoryType> dataSource={histories}>
                            <Column title={t("date")} dataIndex="date" key="date" />
                            <Column title={`${t("amount")} (RM)`} dataIndex="amount" key="amount" />
                            <Column
                                title={t("status")}
                                key="status"
                                render={(_: any, record: HistoryType) => (
                                    <Tag className='m-0 mb-1' color={record.status === "completed" ? "green" : (
                                        record.status === "rejected" ? "red" : "gold"
                                    )}>{t(record.status)?.toUpperCase()}</Tag>
                                )}
                            />
                        </Table>
                    </div>
                </div>
            </Container>
        </AppShell>
    );
}

export default WithdrawalHistoryPage;